import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon, isWeb } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import { Lightning } from "@xsolla/xui-icons-base";
import type { QuestCardProps, QuestCardState, QuestCardStatus } from "./types";

const CARD_PADDING = 12;
const CARD_RADIUS = 8;
const CONTENT_GAP = 8;
const ICON_CONTAINER_SIZE = 48;
const ICON_CONTAINER_RADIUS = 4;
const ICON_SIZE = 20;
const BADGE_SIZE = 12;
const BADGE_ICON_SIZE = 8;
const TITLE_FONT_SIZE = 14;
const TITLE_LINE_HEIGHT = 18;
const BODY_FONT_SIZE = 11;
const BODY_LINE_HEIGHT = 14;

const DIMMED_STATES: QuestCardState[] = ["completed", "expired"];

function mapLegacyStatus(status: QuestCardStatus): QuestCardState {
  switch (status) {
    case "success":
      return "completed";
    case "warning":
    case "alert":
      return "expiring";
    default:
      return "default";
  }
}

function warnDeprecated(prop: string, replacement: string) {
  console.warn(
    `[QuestCard] "${prop}" is deprecated. Use "${replacement}" instead.`
  );
}

export const QuestCard: React.FC<QuestCardProps> = ({
  title,
  description,
  caption,
  questState: questStateProp,
  icon,
  trailing,
  onPress,
  className,
  testID,
  // deprecated
  subtitle,
  status,
  statusIcon,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  if (subtitle !== undefined && description === undefined) {
    warnDeprecated("subtitle", "description");
  }
  if (status !== undefined && questStateProp === undefined) {
    warnDeprecated("status", "questState");
  }
  if (statusIcon !== undefined) {
    warnDeprecated("statusIcon", "questState");
  }

  const resolvedDescription = description ?? subtitle;
  const resolvedState: QuestCardState =
    questStateProp ?? (status ? mapLegacyStatus(status) : "default");

  const isDimmed = DIMMED_STATES.includes(resolvedState);
  const hasBorder = resolvedState === "default" || resolvedState === "expiring";
  const showBadge = resolvedState === "expiring";

  const wrapperProps = onPress ? { onPress } : {};

  return (
    <Box
      className={className}
      testID={testID}
      backgroundColor={theme.colors.overlay.mono}
      borderRadius={CARD_RADIUS}
      padding={CARD_PADDING}
      flexDirection="row"
      alignItems="center"
      overflow="hidden"
      style={
        isWeb
          ? {
              borderRadius: `${CARD_RADIUS}px`,
              border: hasBorder
                ? `1px solid ${theme.colors.border.secondary}`
                : "none",
              opacity: isDimmed ? 0.48 : 1,
              cursor: onPress ? "pointer" : "default",
            }
          : {
              borderRadius: CARD_RADIUS,
              borderWidth: hasBorder ? 1 : 0,
              borderColor: hasBorder
                ? theme.colors.border.secondary
                : "transparent",
              opacity: isDimmed ? 0.48 : 1,
            }
      }
      {...wrapperProps}
    >
      {/* Left icon slot */}
      {icon && (
        <Box
          width={ICON_CONTAINER_SIZE}
          height={ICON_CONTAINER_SIZE}
          backgroundColor={theme.colors.overlay.mono}
          borderRadius={ICON_CONTAINER_RADIUS}
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          style={isWeb ? { position: "relative" } : { position: "relative" }}
        >
          <Icon size={ICON_SIZE} color={theme.colors.content.primary}>
            {icon}
          </Icon>

          {/* Expiring badge */}
          {showBadge && (
            <Box
              width={BADGE_SIZE}
              height={BADGE_SIZE}
              backgroundColor={theme.colors.content.alert.secondary}
              borderRadius={BADGE_SIZE / 2}
              alignItems="center"
              justifyContent="center"
              style={
                isWeb
                  ? { position: "absolute", top: -4, right: -4 }
                  : { position: "absolute", top: -4, right: -4 }
              }
            >
              <Icon size={BADGE_ICON_SIZE} color="#ffffff">
                <Lightning />
              </Icon>
            </Box>
          )}
        </Box>
      )}

      {/* Text content */}
      <Box
        flex={1}
        flexDirection="column"
        gap={0}
        marginLeft={icon ? CONTENT_GAP : 0}
        style={{ minWidth: 0, minHeight: 1 }}
      >
        <Text
          color={theme.colors.content.primary}
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          fontWeight="400"
          numberOfLines={1}
          style={
            isWeb
              ? {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }
              : undefined
          }
        >
          {title}
        </Text>

        {resolvedDescription && (
          <Text
            color={theme.colors.content.tertiary}
            fontSize={BODY_FONT_SIZE}
            lineHeight={BODY_LINE_HEIGHT}
            fontWeight="400"
            numberOfLines={1}
            style={
              isWeb
                ? {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }
                : undefined
            }
          >
            {resolvedDescription}
          </Text>
        )}

        {caption && (
          <Text
            color={theme.colors.content.tertiary}
            fontSize={BODY_FONT_SIZE}
            lineHeight={BODY_LINE_HEIGHT}
            fontWeight="400"
            numberOfLines={1}
            style={
              isWeb
                ? {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    ...(resolvedDescription ? { paddingTop: 2 } : {}),
                  }
                : resolvedDescription
                  ? { paddingTop: 2 }
                  : undefined
            }
          >
            {caption}
          </Text>
        )}
      </Box>

      {/* Trailing slot */}
      {trailing && (
        <Box marginLeft={CONTENT_GAP} flexShrink={0}>
          {trailing}
        </Box>
      )}
    </Box>
  );
};
